/**
 * ``useTrail`` — TanStack Query hook around
 * ``GET /api/query/trail/{producer_id}?start_time=&end_time=``.
 *
 * Stream A PR 2 / commit M5 — REQ-UI-012's data source. The hook
 * is enabled only when a Producer is selected; selection is owned
 * by the module-level Zustand store
 * (:file:`@/selection/selection-store`). :class:`TrailLayer` feeds this
 * hook the ``mapSelected``-slot platform id via :func:`useMapSelectedProducerId`
 * (the trail follows the map selection per the 2026-06-09 two-slot
 * redesign; REQ-UI-027 + ADR-024 §5).
 *
 * # Window
 *
 * 15 minutes per ADR-016 §5 + Open Item 3 default (accepted
 * 2026-05-12 prereqs PR). The hook computes ``start_time`` =
 * ``now − 15 min`` and ``end_time`` = ``now`` at query time. The
 * window is stable per render — re-computing on every render
 * would invalidate the cache key continuously and re-fire the
 * fetch. The window anchor is captured at the moment the selected
 * Producer changes; while a Producer remains selected, the same
 * window is queried (within TanStack's ``staleTime``).
 *
 * # Stale time
 *
 * 10 seconds. M5's trail is fetched once on selection-change and
 * cached; SSE deltas don't yet trigger a re-fetch (the trail
 * extension via live updates is a post-MVP enhancement per
 * ADR-016 §5 "Updates when new positions arrive via the SSE
 * stream" — flagged below as a follow-up seam). The 10-second
 * stale-time means re-selecting the same Producer within 10
 * seconds re-uses the cached trail; longer than that, a fresh
 * fetch confirms recency.
 *
 * # SSE live-update seam
 *
 * Per ADR-016 §5 the trail "updates when new positions arrive via
 * the SSE stream". That requires either:
 *
 *   1. Reading the snapshot layer's SSE message stream and
 *      appending the matching Producer's position to the trail
 *      cache, OR
 *   2. Invalidating the trail query on each SSE status delta for
 *      the selected Producer (cheaper to implement but more
 *      network).
 *
 * M5 ships option 0: fetch once on selection-change; no live
 * updates. Option 1 is the M5+1 follow-up seam (low-priority
 * because most MVP-target Producers are stationary per ADR-016
 * §5). Flagged here for the orchestrator's review.
 *
 * # Role gating
 *
 * Same as :func:`useSnapshot` — the BFF gates ``/api/query/trail``
 * on the map-reader role bundle. The UI mirrors with a soft
 * assertion through :const:`MAP_READER_ROLES`.
 *
 * # Cancellation
 *
 * TanStack Query cancels the fetch when the component unmounts
 * (via the ``AbortSignal`` plumbed through ``apiFetch``). The
 * hook also re-fires the query when the selected Producer
 * changes, which means the in-flight request for the prior
 * Producer is correctly aborted — TanStack handles this via the
 * query-key change.
 *
 * Cross-references:
 *   - REQ-UI-012 (trail rendering).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - REQ-AUTH-022 / REQ-AUTH-024..027 (RBAC mirroring).
 *   - ADR-016 §5 + Open Item 3 / Open Item 4.
 *   - packages/meridian_bff/src/meridian_bff/_query_routes.py
 *     §trail_route.
 *   - packages/meridian_bff/api/openapi.yaml §TrailResponse.
 */
import { useMemo } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import { MAP_READER_ROLES } from "@/map/use-snapshot";
import { isTrailResponse, type TrailResponse } from "@/map/trail-types";

/**
 * Trail window length in milliseconds. ADR-016 Open Item 3 default
 * (15 minutes) accepted 2026-05-12 in the prereqs PR. Exported so
 * tests can reach the value without re-deriving + so future
 * operator-preference plumbing can override per session.
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - ADR-016 §5 / Open Item 3.
 */
export const TRAIL_WINDOW_MS = 15 * 60 * 1000;

/**
 * Default :class:`useQuery` stale time for the trail, in
 * milliseconds. See module docstring §"Stale time".
 */
export const TRAIL_STALE_TIME_MS = 10_000;

/**
 * Compose the BFF URL for a producer's trail.
 *
 * Exported for unit-test reach into the query-string composition
 * without spinning up the full hook + msw server. Mirrors
 * :func:`composeSnapshotUrl` in :file:`use-snapshot.ts`.
 *
 * @param producerId - Producer to fetch.
 * @param startTime - Window start (inclusive per BFF contract).
 * @param endTime - Window end (inclusive per BFF contract).
 */
export function composeTrailUrl(producerId: string, startTime: Date, endTime: Date): string {
  const params = new URLSearchParams({
    start_time: startTime.toISOString(),
    end_time: endTime.toISOString(),
  });
  // ``producerId`` is path-segmented and must be URI-encoded —
  // SAPIENT Producer IDs are alphanumeric per the wire schema, but
  // belt-and-suspenders encoding survives a future schema change.
  return `/api/query/trail/${encodeURIComponent(producerId)}?${params.toString()}`;
}

/**
 * The query-key shape — exported so consumers
 * (:func:`useQueryClient().invalidateQueries`) can target it.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export function trailQueryKey(
  producerId: string,
  startTime: Date,
  endTime: Date,
): readonly unknown[] {
  return ["trail", producerId, startTime.toISOString(), endTime.toISOString()];
}

/**
 * Options for :func:`useTrail`.
 */
export interface UseTrailOptions {
  /** Producer to fetch. ``null`` disables the query (no Producer
   *  selected). */
  producerId: string | null;
  /**
   * Override the trail window length in milliseconds. Tests use
   * this to exercise short windows without re-deriving anchors;
   * production callers leave the default (15 min per Open Item 3).
   */
  windowMs?: number;
  /** Override the stale time. Tests set 0; production omits. */
  staleTimeMs?: number;
  /**
   * Override the "now" anchor — only useful for tests that need a
   * deterministic window. Production callers omit; the hook
   * captures ``new Date()`` at the moment ``producerId`` changes.
   */
  nowOverride?: Date;
}

/**
 * The :func:`useTrail` result. ``UseQueryResult`` carries
 * ``data | error | isPending | isError | isSuccess | refetch`` —
 * the hook does not narrow it.
 */
export type UseTrailResult = UseQueryResult<TrailResponse, ApiError>;

/**
 * Issue a trail request and surface the result as a
 * :class:`UseQueryResult`.
 *
 * The query is disabled when ``producerId`` is ``null`` (no
 * Producer selected) or the session lacks a map-reader role.
 *
 * @param options - Producer ID + optional overrides.
 * @returns TanStack Query result.
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - REQ-UI-026.
 *   - REQ-AUTH-022 / 024..027.
 */
export function useTrail(options: UseTrailOptions): UseTrailResult {
  const session = useSession();
  const hasReadRole = session.hasAnyRole(MAP_READER_ROLES);
  const windowMs = options.windowMs ?? TRAIL_WINDOW_MS;
  const { producerId, nowOverride } = options;

  // Anchor the window per ``producerId`` change. ``useMemo`` keyed
  // on ``producerId`` captures ``new Date()`` at the moment the
  // selection changes; while the same Producer remains selected,
  // the anchor (and therefore the query-key) is stable. This
  // prevents the cache-key-thrashes-on-every-render trap.
  //
  // The memo body reads ``producerId`` (via the closure-pinned
  // ``anchorKey`` below) so the exhaustive-deps lint rule
  // recognizes the producerId-keyed re-anchor as intentional. The
  // ``anchorKey`` value is otherwise discarded — its only purpose is
  // to expose the producerId to the lint rule's flow analysis.
  //
  // ``nowOverride`` exists so tests don't have to mock ``Date``;
  // production omits and the memo captures the real wall clock.
  const { startTime, endTime } = useMemo(() => {
    // Read producerId in-body so the deps array is honest. Even when
    // null, the read participates in the memo identity.
    const anchorKey = producerId;
    void anchorKey;
    const now = nowOverride ?? new Date();
    const start = new Date(now.getTime() - windowMs);
    return { startTime: start, endTime: now };
  }, [producerId, windowMs, nowOverride]);

  const enabled = producerId !== null && hasReadRole;

  return useQuery<TrailResponse, ApiError>({
    queryKey:
      producerId !== null ? trailQueryKey(producerId, startTime, endTime) : ["trail", "disabled"],
    queryFn: async ({ signal }) => {
      if (producerId === null) {
        // ``enabled: false`` should prevent this branch, but the
        // type system requires queryFn to be unconditional. Per
        // CLAUDE.md §8.1 (operator-visibility), surface loudly.
        throw new ApiError(0, null, "useTrail: query ran without producerId");
      }
      const url = composeTrailUrl(producerId, startTime, endTime);
      const raw = await apiFetch<unknown>(url, { signal });
      if (!isTrailResponse(raw)) {
        // Boundary-validation failure. The BFF surface shifted —
        // halt with a structured error per CLAUDE.md §8.1 (silent
        // failures forbidden).
        console.error("[meridian/map/use-trail] trail response failed shape validation", raw);
        throw new ApiError(500, raw, "trail response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: options.staleTimeMs ?? TRAIL_STALE_TIME_MS,
    // Retry transient errors twice; never retry on auth failures
    // (the global QueryClient filters 401/403). Local override here
    // keeps the hook self-contained for tests that construct an
    // isolated QueryClient.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
